/**
 * MunicipalitiesPage — super_admin: registro Comuni + onboarding (Sprint 4).
 * Elenco + ricerca, provisioning nuovo Comune (+ primo admin, password una-tantum),
 * e accesso alla Configurazione del Comune selezionato (riusa MunicipalityConfig).
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Building2, Globe, Lock, Plus, Settings, Loader2, Search } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
        Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { listMunicipalities, provisionMunicipality } from '@/services/api/admin';
import { formatApiError } from '@/services/api/client';
import { DASH, MUNI } from '@/constants/testIds';

const slugify = (s) =>
        s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const EMPTY = {
        name: '', slug: '', region: '', province: '', description: '', is_public: false,
        admin_full_name: '', admin_email: '',
};

export default function MunicipalitiesPage() {
        const qc = useQueryClient();
        const navigate = useNavigate();
        const [search, setSearch] = useState('');
        const [form, setForm] = useState(null);
        const [secret, setSecret] = useState(null); // { email, temporary_password }

        const q = useQuery({
                queryKey: ['municipalities', search],
                queryFn: () => listMunicipalities(search),
        });
        const list = q.data ?? [];

        const createMut = useMutation({
                mutationFn: provisionMunicipality,
                onSuccess: (res) => {
                        setForm(null);
                        setSecret({ email: res.admin.email, temporary_password: res.admin.temporary_password });
                        toast.success('Comune creato');
                        qc.invalidateQueries({ queryKey: ['municipalities'] });
                },
                onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
        });

        const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
        const submit = () => {
                const body = {
                        name: form.name,
                        slug: form.slug || slugify(form.name),
                        region: form.region || undefined,
                        province: form.province || undefined,
                        description: form.description || undefined,
                        is_public: form.is_public,
                        admin_full_name: form.admin_full_name,
                        admin_email: form.admin_email,
                };
                createMut.mutate(body);
        };

        const canSubmit = form && form.name && form.admin_full_name && form.admin_email;

        return (
                <div data-testid={MUNI.page} className="mx-auto max-w-4xl">
                        <div className="flex flex-wrap items-end justify-between gap-4">
                                <div>
                                        <h1 className="text-3xl font-extrabold sm:text-4xl" style={{ fontFamily: "'Poppins', sans-serif" }}>Comuni</h1>
                                        <p className="mt-1 text-slate-400">Registro globale e onboarding (super_admin).</p>
                                </div>
                                <Button data-testid={MUNI.createButton} onClick={() => setForm({ ...EMPTY })}
                                        className="bg-emerald-400 text-slate-950 hover:bg-emerald-300">
                                        <Plus size={16} className="mr-1" /> Nuovo Comune
                                </Button>
                        </div>

                        <div className="relative mt-6">
                                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                                <Input data-testid={MUNI.searchInput} placeholder="Cerca per nome o slug…"
                                        value={search} onChange={(e) => setSearch(e.target.value)}
                                        className="border-slate-700 bg-slate-950/50 pl-9 text-slate-100" />
                        </div>

                        <div data-testid={DASH.municipalitiesSection} className="mt-6 grid gap-3 sm:grid-cols-2">
                                {q.isLoading && <p className="text-slate-500">Caricamento…</p>}
                                {!q.isLoading && list.length === 0 && <p className="text-slate-500">Nessun Comune trovato.</p>}
                                {list.map((m) => (
                                        <div key={m.id} data-testid={`municipality-row-${m.id}`} className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
                                                <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                                <Building2 size={18} className="text-emerald-300" />
                                                                <span className="font-semibold">{m.name}</span>
                                                        </div>
                                                        {m.is_public
                                                                ? <Badge className="bg-emerald-400/10 text-emerald-300"><Globe size={12} className="mr-1" /> Pubblico</Badge>
                                                                : <Badge className="bg-slate-700/40 text-slate-400"><Lock size={12} className="mr-1" /> Privato</Badge>}
                                                </div>
                                                <p className="mt-2 text-xs text-slate-500">{[m.province, m.region].filter(Boolean).join(' · ')} · {m.slug} · {m.status}</p>
                                                <Button size="sm" variant="outline" data-testid={`${MUNI.configureButton}-${m.id}`}
                                                        onClick={() => navigate(`/admin/config?municipality_id=${m.id}`)}
                                                        className="mt-3 border-slate-600 text-slate-300 hover:bg-slate-800">
                                                        <Settings size={13} className="mr-1" /> Configura
                                                </Button>
                                        </div>
                                ))}
                        </div>

                        {/* Provisioning dialog */}
                        <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
                                <DialogContent data-testid={MUNI.dialog} className="max-h-[90vh] overflow-y-auto border-slate-800 bg-slate-950 text-slate-100">
                                        <DialogHeader><DialogTitle>Nuovo Comune</DialogTitle>
                                                <DialogDescription className="sr-only">Modulo di provisioning di un nuovo Comune e del suo primo amministratore.</DialogDescription>
                                        </DialogHeader>
                                        {form && (
                                                <div className="space-y-4">
                                                        <p className="text-xs uppercase tracking-widest text-emerald-300">Comune</p>
                                                        <div className="grid gap-3 sm:grid-cols-2">
                                                                <div>
                                                                        <Label className="text-xs text-slate-400">Nome</Label>
                                                                        <Input data-testid={MUNI.nameInput} value={form.name}
                                                                                onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, name: v, slug: f.slug || slugify(v) })); }}
                                                                                className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
                                                                </div>
                                                                <div>
                                                                        <Label className="text-xs text-slate-400">Slug</Label>
                                                                        <Input data-testid={MUNI.slugInput} value={form.slug}
                                                                                onChange={(e) => set('slug', slugify(e.target.value))}
                                                                                className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
                                                                </div>
                                                                <div>
                                                                        <Label className="text-xs text-slate-400">Regione</Label>
                                                                        <Input data-testid={MUNI.regionInput} value={form.region}
                                                                                onChange={(e) => set('region', e.target.value)}
                                                                                className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
                                                                </div>
                                                                <div>
                                                                        <Label className="text-xs text-slate-400">Provincia</Label>
                                                                        <Input data-testid={MUNI.provinceInput} value={form.province}
                                                                                onChange={(e) => set('province', e.target.value)}
                                                                                className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
                                                                </div>
                                                        </div>
                                                        <div>
                                                                <Label className="text-xs text-slate-400">Descrizione (opzionale)</Label>
                                                                <Input data-testid={MUNI.descriptionInput} value={form.description}
                                                                        onChange={(e) => set('description', e.target.value)}
                                                                        className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
                                                        </div>
                                                        <div className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                                                                <span className="text-sm text-slate-200">Pubblico (visibile nel sito pubblico)</span>
                                                                <Switch data-testid={MUNI.isPublicSwitch} checked={form.is_public} onCheckedChange={(v) => set('is_public', v)} />
                                                        </div>

                                                        <p className="pt-2 text-xs uppercase tracking-widest text-emerald-300">Primo amministratore</p>
                                                        <div className="grid gap-3 sm:grid-cols-2">
                                                                <div>
                                                                        <Label className="text-xs text-slate-400">Nome completo</Label>
                                                                        <Input data-testid={MUNI.adminNameInput} value={form.admin_full_name}
                                                                                onChange={(e) => set('admin_full_name', e.target.value)}
                                                                                className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
                                                                </div>
                                                                <div>
                                                                        <Label className="text-xs text-slate-400">Email</Label>
                                                                        <Input data-testid={MUNI.adminEmailInput} type="email" value={form.admin_email}
                                                                                onChange={(e) => set('admin_email', e.target.value)}
                                                                                className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
                                                                </div>
                                                        </div>
                                                        <p className="text-xs text-slate-500">Verrà generata una password temporanea per l'amministratore, mostrata una sola volta.</p>
                                                </div>
                                        )}
                                        <DialogFooter>
                                                <Button variant="ghost" onClick={() => setForm(null)} className="text-slate-400">Annulla</Button>
                                                <Button data-testid={MUNI.submitButton} onClick={submit} disabled={!canSubmit || createMut.isPending}
                                                        className="bg-emerald-400 text-slate-950 hover:bg-emerald-300">
                                                        {createMut.isPending && <Loader2 size={15} className="mr-1 animate-spin" />} Crea Comune
                                                </Button>
                                        </DialogFooter>
                                </DialogContent>
                        </Dialog>

                        {/* Temporary password (shown once) */}
                        <Dialog open={!!secret} onOpenChange={(o) => !o && setSecret(null)}>
                                <DialogContent data-testid={MUNI.secretDialog} className="border-slate-800 bg-slate-950 text-slate-100">
                                        <DialogHeader><DialogTitle>Password temporanea amministratore</DialogTitle>
                                                <DialogDescription className="sr-only">Password temporanea dell'amministratore, visibile una sola volta.</DialogDescription>
                                        </DialogHeader>
                                        <div className="space-y-3">
                                                <p className="text-sm text-slate-400">Comunica questa password a <span className="text-slate-200">{secret?.email}</span>. Non sarà più visibile dopo la chiusura.</p>
                                                <code data-testid={MUNI.secretValue} className="block rounded-lg border border-emerald-400/30 bg-emerald-400/10 px-4 py-3 text-center text-lg font-bold tracking-wider text-emerald-300">
                                                        {secret?.temporary_password}
                                                </code>
                                        </div>
                                        <DialogFooter>
                                                <Button data-testid={MUNI.secretCloseButton} onClick={() => setSecret(null)}
                                                        className="bg-emerald-400 text-slate-950 hover:bg-emerald-300">Ho copiato la password</Button>
                                        </DialogFooter>
                                </DialogContent>
                        </Dialog>
                </div>
        );
}
